import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["className", "visible", "children", "maskStyle", "contentStyle", "direction", "percentToClose", "distanceToClose", "speedToClose", "allowSwipeDirections", "onPreventTouchMove", "onTouchStart", "onTouchEnd", "onOpen", "onClose", "close"];
import React, { useRef, forwardRef, useImperativeHandle, useCallback, useState, useEffect, useMemo } from 'react';
import { cls, componentWrapper, nextTick } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import Popup from '../popup';
import { open } from './methods';
import { getStyleWithVendor, useRefState } from '../_helpers';
var PopupSwiper = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      visible = props.visible,
      children = props.children,
      maskStyle = props.maskStyle,
      contentStyle = props.contentStyle,
      _props$direction = props.direction,
      direction = _props$direction === void 0 ? 'bottom' : _props$direction,
      _props$percentToClose = props.percentToClose,
      percentToClose = _props$percentToClose === void 0 ? 0.3 : _props$percentToClose,
      _props$distanceToClos = props.distanceToClose,
      distanceToClose = _props$distanceToClos === void 0 ? 10 : _props$distanceToClos,
      _props$speedToClose = props.speedToClose,
      speedToClose = _props$speedToClose === void 0 ? 200 : _props$speedToClose,
      allowSwipeDirections = props.allowSwipeDirections,
      onPreventTouchMove = props.onPreventTouchMove,
      onTouchStart = props.onTouchStart,
      onTouchEnd = props.onTouchEnd,
      _onOpen = props.onOpen,
      _onClose = props.onClose,
      close = props.close,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded);

  var _useState = useState(visible),
      opened = _useState[0],
      setOpened = _useState[1];

  var _useRefState = useRefState({
    direction: 'X',
    value: 0
  }),
      distance = _useRefState[0],
      distanceRef = _useRefState[1],
      setDistance = _useRefState[2];

  var _useState2 = useState(false),
      hasTrans = _useState2[0],
      setHasTrans = _useState2[1];

  var popupRef = useRef(null);
  var touchStartXRef = useRef(0);
  var touchStartYRef = useRef(0);
  var touchStartTimeRef = useRef(0);
  var hasTouchStartRef = useRef(false);
  var getContentSize = useCallback(function (direc) {
    var _popupRef$current;

    var contentDom = (_popupRef$current = popupRef.current) == null ? void 0 : _popupRef$current.content;
    var contentWidth = (contentDom == null ? void 0 : contentDom.offsetWidth) || 0;
    var contentHeight = (contentDom == null ? void 0 : contentDom.offsetHeight) || 0;
    var contentSize = direc === 'X' ? contentWidth : contentHeight;
    return contentSize;
  }, []);
  var getPercent = useCallback(function (dis) {
    var direc = dis.direction,
        value = dis.value;
    var contentSize = getContentSize(direc);
    return contentSize ? Math.max(0, Math.min(1, Math.abs(value) / contentSize)) : 0;
  }, []);
  var percent = useMemo(function () {
    return getPercent(distance);
  }, [distance]);
  var allowedDirections = useMemo(function () {
    return allowSwipeDirections !== void 0 ? allowSwipeDirections : [direction];
  }, [allowSwipeDirections, direction]);
  var handleTouchStart = useCallback(function (e) {
    var _e$touches, _e$touches$, _e$touches2, _e$touches2$;

    if (onTouchStart && onTouchStart(e)) {
      return;
    }

    setHasTrans(false);
    hasTouchStartRef.current = true;
    touchStartXRef.current = ((_e$touches = e.touches) == null ? void 0 : (_e$touches$ = _e$touches[0]) == null ? void 0 : _e$touches$.clientX) || 0;
    touchStartYRef.current = ((_e$touches2 = e.touches) == null ? void 0 : (_e$touches2$ = _e$touches2[0]) == null ? void 0 : _e$touches2$.clientY) || 0;
    touchStartTimeRef.current = new Date().getTime();
  }, []);
  var getMovingFromDirection = useCallback(function (direc, disX, disY) {
    if (direc === 'X') {
      return disX > 0 ? 'right' : 'left';
    }

    return disY > 0 ? 'bottom' : 'top';
  }, []);
  var handlePreventTouchMove = useCallback(function (e, direc) {
    var _popupRef$current2, _e$changedTouches, _e$changedTouches$, _e$changedTouches2, _e$changedTouches2$;

    if (onPreventTouchMove && onPreventTouchMove(e, direc)) {
      return;
    }

    if (!hasTouchStartRef.current || e.target === ((_popupRef$current2 = popupRef.current) == null ? void 0 : _popupRef$current2.mask)) {
      return;
    }

    var touchMoveX = ((_e$changedTouches = e.changedTouches) == null ? void 0 : (_e$changedTouches$ = _e$changedTouches[0]) == null ? void 0 : _e$changedTouches$.clientX) || 0;
    var touchMoveY = ((_e$changedTouches2 = e.changedTouches) == null ? void 0 : (_e$changedTouches2$ = _e$changedTouches2[0]) == null ? void 0 : _e$changedTouches2$.clientY) || 0;
    var disX = touchMoveX < 0 ? 0 : touchMoveX - touchStartXRef.current;
    var disY = touchMoveY - touchStartYRef.current;
    var fromDirec = getMovingFromDirection(direc === 'x' ? 'X' : 'Y', disX, disY);
    var movingFromDirec = allowedDirections.indexOf(fromDirec) >= 0 ? fromDirec : '';

    switch (movingFromDirec) {
      case 'top':
      case 'bottom':
        setDistance({
          direction: 'Y',
          value: Math[movingFromDirec === 'top' ? 'min' : 'max'](0, disY)
        });
        break;

      case 'left':
      case 'right':
        setDistance({
          direction: 'X',
          value: Math[movingFromDirec === 'left' ? 'min' : 'max'](0, disX)
        });
        break;

      default:
        break;
    }
  }, [onPreventTouchMove, direction]);
  var getDescDisFromDirection = useCallback(function (direc, dis) {
    var fromDirec = getMovingFromDirection(direc, dis, dis);
    var contentSize = getContentSize(direc);
    var prefix = fromDirec === 'left' || fromDirec === 'top' ? -1 : 1;
    return prefix * contentSize;
  }, []);
  var handleTouchEnd = useCallback(function (e) {
    if (onTouchEnd && onTouchEnd(e)) {
      return;
    }

    hasTouchStartRef.current = false;
    var touchEndTime = new Date().getTime();
    var _distanceRef$current = distanceRef.current,
        direc = _distanceRef$current.direction,
        value = _distanceRef$current.value;
    var per = getPercent(distanceRef.current);
    var speed = value / (touchEndTime - touchStartTimeRef.current) * 1000;

    if (per > 0 && per >= percentToClose && Math.abs(value) > distanceToClose || Math.abs(speed) > speedToClose) {
      setHasTrans(false);
      close(e);
      nextTick(function () {
        setDistance({
          direction: direc,
          value: getDescDisFromDirection(direc, value)
        });
      });
    } else {
      setHasTrans(true);
      setDistance({
        direction: 'X',
        value: 0
      });
    }

    touchStartTimeRef.current = 0;
  }, [percentToClose, distanceToClose, speedToClose, close]);
  useImperativeHandle(ref, function () {
    return popupRef.current;
  });
  useEffect(function () {
    var _popupRef$current3;

    var contentDom = (_popupRef$current3 = popupRef.current) == null ? void 0 : _popupRef$current3.content;

    if (opened && contentDom) {
      contentDom.addEventListener('touchstart', handleTouchStart);
      contentDom.addEventListener('touchend', handleTouchEnd);
      contentDom.addEventListener('touchcancel', handleTouchEnd);
    }

    return function () {
      var _popupRef$current4;

      var ctDom = (_popupRef$current4 = popupRef.current) == null ? void 0 : _popupRef$current4.content;

      if (opened && ctDom) {
        ctDom.removeEventListener('touchstart', handleTouchStart);
        ctDom.removeEventListener('touchend', handleTouchEnd);
        ctDom.removeEventListener('touchcancel', handleTouchEnd);
      }
    };
  }, [opened, handleTouchStart, handleTouchEnd]);

  function getContentStyle() {
    var disStyle = getStyleWithVendor({
      transform: distance.value ? "translate" + distance.direction + "(" + distance.value + "px) translateZ(2px)" : void 0
    });
    return _extends({}, contentStyle || {}, disStyle);
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement(Popup, _extends({
      ref: popupRef,
      visible: visible,
      close: close,
      className: cls(prefixCls + "-popup-swiper", className, {
        'has-trans': hasTrans
      }),
      direction: direction,
      onPreventTouchMove: handlePreventTouchMove,
      onOpen: function onOpen() {
        setOpened(true);
        _onOpen == null ? void 0 : _onOpen();
      },
      onClose: function onClose() {
        setOpened(false);
        setDistance({
          direction: 'X',
          value: 0
        });
        _onClose == null ? void 0 : _onClose();
      },
      maskStyle: _extends({}, maskStyle || {}, {
        opacity: percent ? 1 - percent : void 0
      }),
      contentStyle: getContentStyle()
    }, otherProps), children);
  });
});
export function methodsGenerator(Comp) {
  return {
    /**
     * 打开弹出层
     * @param {string | PopupSwiperProps} config 配置
     * @returns {{ close: () => void; update: (newConfig: PopupSwiperProps) => void; }}
     */
    open: open(Comp)
  };
}
/**
 * 基于弹出层(Popup)封装的具有手势关闭功能的弹出层组件，拥有 Popup 组件的其他能力。
 * @type 反馈
 * @name 可滑动弹出层
 */

export default componentWrapper(PopupSwiper, methodsGenerator(PopupSwiper));